/* ev-card-interactions.js — shared behavior for every .evc__card on the site
   (collection, search, bestseller, related products, ...).

   Provides:
   - Reveal-on-scroll (.is-visible)
   - Three.js hover shader + 3D tilt (desktop, non-reduced-motion only)
   - Keyboard activation (Enter/Space) for .evc__cart-btn
   - window.evcATC / window.evcBuyNow — add-to-cart / buy-now handlers used by
     the onclick="" attributes baked into snippets/ev-product-card.liquid

   window.evcInitCardInteractions(root) wires up any not-yet-initialized
   .evc__card under `root` (default: document). Safe to call repeatedly —
   already-initialized cards are skipped via a data-evc-inited flag, so it can
   be called again after cards are injected dynamically (e.g. a carousel that
   loads more products, or a client-rendered list). */
(function () {
  "use strict";

  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  /* Positive capability query, mirroring the CSS that hides .evc__shader at
     (hover: none), (max-width: 1023px).
     Do NOT gate on "ontouchstart"/maxTouchPoints: a touchscreen laptop reports
     maxTouchPoints > 0 while its primary pointer is a mouse, which silently
     killed the hover smoke on desktop. ev-models.js never had that check —
     that is why the Models carousel smoked and this one did not. */
  var shaderEnabled = window.matchMedia(
    "(hover: hover) and (pointer: fine) and (min-width: 1024px) and (prefers-reduced-motion: no-preference)",
  ).matches;
  var _evcCardCounter = 0;
  var active = {},
    leaveTimers = {};
  /* Each hover spins up its own THREE.WebGLRenderer (its own GL context).
     Browsers cap live WebGL contexts per page (commonly ~16); a fast swipe
     across a carousel can fire mouseenter/mouseleave on many cards within
     the 550ms teardown delay, piling up contexts faster than they're freed.
     Once the cap is hit, new/old contexts start failing or getting silently
     lost, which makes THREE throw deep inside render() every single frame —
     and since requestAnimationFrame(animate) below is scheduled before that
     throw, the loop never stops on its own. Cap concurrency and always kill
     the oldest shader first so we never approach that ceiling. */
  var MAX_ACTIVE_SHADERS = 4;
  var activeOrder = [];

  function initReveal(cards) {
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      cards.forEach(function (c) {
        c.classList.add("is-visible");
      });
      return;
    }
    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            var idx = Array.prototype.indexOf.call(cards, e.target);
            setTimeout(
              function () {
                e.target.classList.add("is-visible");
              },
              Math.min(idx, 12) * 40,
            );
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px 100px 0px" },
    );
    cards.forEach(function (c) {
      obs.observe(c);
    });
  }

  var FS = [
    "precision mediump float;",
    "uniform vec2 u_resolution;uniform float u_time;uniform float u_hue;",
    "vec3 hsv2rgb(vec3 c){vec4 K=vec4(1.0,2.0/3.0,1.0/3.0,3.0);vec3 p=abs(fract(c.xxx+K.xyz)*6.0-K.www);return c.z*mix(K.xxx,clamp(p-K.xxx,0.0,1.0),c.y);}",
    "float rnd(vec2 st){return fract(sin(dot(st,vec2(12.9898,78.233)))*43758.5453);}",
    "float ns(vec2 st){vec2 i=floor(st);vec2 f=fract(st);vec2 u=f*f*(3.0-2.0*f);return mix(mix(rnd(i),rnd(i+vec2(1,0)),u.x),mix(rnd(i+vec2(0,1)),rnd(i+vec2(1,1)),u.x),u.y);}",
    "float fbm(vec2 st){float v=0.0;float a=0.5;for(int i=0;i<6;i++){v+=a*ns(st);st*=2.0;a*=0.5;}return v;}",
    "mat2 rot(float a){return mat2(cos(a),-sin(a),sin(a),cos(a));}",
    "void main(){",
    "vec2 uv=(gl_FragCoord.xy*2.0-u_resolution.xy)/min(u_resolution.x,u_resolution.y);",
    "float t=u_time;vec2 p=uv*rot(t*0.15);",
    "float n1=fbm(p*1.2+vec2(t*0.12,t*0.22));",
    "float n2=fbm(p*2.0+n1+vec2(-t*0.28,t*0.18));",
    "float n3=fbm(p*3.2+n2+vec2(t*0.12,-t*0.22));",
    "float fn=n1*0.6+n2*0.25+n3*0.15;",
    "float s=0.65+fn*0.35;float v=0.12+pow(fn,2.2)*1.8;v+=pow(smoothstep(0.65,1.0,fn),2.5)*1.2;",
    "vec3 col=hsv2rgb(vec3(u_hue/360.0+fn*0.12,s,v));",
    "col*=1.0-smoothstep(0.5,1.4,length(uv));",
    "float alpha=smoothstep(0.02,0.15,length(col))*0.85;",
    "gl_FragColor=vec4(col,alpha);}",
  ].join("\n");
  var VS = "void main(){gl_Position=vec4(position,1.0);}";

  function ensureThree(cb) {
    if (typeof THREE !== "undefined") {
      cb();
      return;
    }
    if (window.__evcThreePending) {
      window.__evcThreeCbs.push(cb);
      return;
    }
    window.__evcThreePending = true;
    window.__evcThreeCbs = [cb];
    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
    s.onload = function () {
      window.__evcThreeCbs.forEach(function (f) {
        f();
      });
      window.__evcThreeCbs = [];
    };
    document.body.appendChild(s);
  }

  function createShader(card, hue) {
    var imgWrap = card.querySelector(".evc__img-wrap");
    var mount = card.querySelector(".evc__shader");
    if (!mount || !imgWrap || typeof THREE === "undefined") return null;
    var w = imgWrap.clientWidth || 200,
      h = imgWrap.clientHeight || 200;
    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    } catch (e) {
      return null;
    }
    renderer.setPixelRatio(1);
    renderer.setSize(Math.round(w * 0.5), Math.round(h * 0.5), false);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    mount.appendChild(renderer.domElement);
    var scene = new THREE.Scene(),
      cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    var uni = {
      u_time: { value: 0 },
      u_resolution: { value: new THREE.Vector2(w * 0.5, h * 0.5) },
      u_hue: { value: hue },
    };
    var geo = new THREE.PlaneGeometry(2, 2);
    var mat = new THREE.ShaderMaterial({
      uniforms: uni,
      vertexShader: VS,
      fragmentShader: FS,
      transparent: true,
    });
    scene.add(new THREE.Mesh(geo, mat));
    var start = performance.now();
    var d = { renderer: renderer, material: mat, geometry: geo, frameId: 0, mount: mount, dead: false };
    function animate() {
      if (d.dead) return;
      d.frameId = requestAnimationFrame(animate);
      uni.u_time.value = (performance.now() - start) / 1000;
      try {
        renderer.render(scene, cam);
      } catch (e) {
        /* Render failed (commonly: too many concurrent WebGL contexts caused
           this one to be lost). Stop looping instead of retrying every frame
           forever — that's what turns one failure into thousands of console
           errors and a janky page. */
        d.dead = true;
        cancelAnimationFrame(d.frameId);
        destroyShader(d);
      }
    }
    animate();
    return d;
  }

  function destroyShader(d) {
    if (!d || d.destroyed) return;
    d.destroyed = true;
    d.dead = true;
    cancelAnimationFrame(d.frameId);
    if (d.mount && d.mount.contains(d.renderer.domElement))
      d.mount.removeChild(d.renderer.domElement);
    d.geometry.dispose();
    d.material.dispose();
    try {
      var gl = d.renderer.getContext();
      if (gl && gl.getExtension)
        gl.getExtension("WEBGL_lose_context") &&
          gl.getExtension("WEBGL_lose_context").loseContext();
      d.renderer.forceContextLoss();
      d.renderer.dispose();
    } catch (_e) {}
  }

  function initHoverShader(cards) {
    if (!shaderEnabled) return;
    /* Homepage-only effect. The WebGL plasma, the 3D tilt, the glass sweep and
       the accent drop-shadow all live in this one handler set, so filtering here
       switches all four off for the collection/search grid — those keep their
       plain CSS hover and never even download three.js. Add other card classes
       to this filter to widen it again. */
    var shaderCards = cards.filter(function (c) {
      return c.classList.contains("evc__card--feature");
    });
    if (!shaderCards.length) return;
    ensureThree(function () {
      shaderCards.forEach(function (card) {
        var inner = card.querySelector(".evc__card-inner");
        var glass = card.querySelector(".evc__glass");
        var img = card.querySelector(".evc__img");
        var shaderEl = card.querySelector(".evc__shader");
        var key = "evc-" + _evcCardCounter++;
        /* data-hue/data-color come from the section's per-block "Card color
           theme" (same contract ev-models.js uses). The rotation is only the
           fallback for a feature card whose block has no theme set. */
        var declaredHue = parseFloat(card.getAttribute("data-hue"));
        var hue = isNaN(declaredHue)
          ? (_evcCardCounter * 37 + 263) % 360
          : declaredHue;
        var accent =
          card.getAttribute("data-color") || "hsl(" + hue + ",70%,60%)";
        var tiltRAF = null;

        card.addEventListener("mouseenter", function () {
          if (leaveTimers[key]) {
            clearTimeout(leaveTimers[key]);
            leaveTimers[key] = null;
          }
          if (!active[key]) {
            while (activeOrder.length >= MAX_ACTIVE_SHADERS) {
              var evictKey = activeOrder.shift();
              if (leaveTimers[evictKey]) {
                clearTimeout(leaveTimers[evictKey]);
                leaveTimers[evictKey] = null;
              }
              if (active[evictKey]) {
                destroyShader(active[evictKey]);
                delete active[evictKey];
              }
            }
            active[key] = createShader(card, hue);
            activeOrder.push(key);
          }
          if (shaderEl) {
            shaderEl.classList.remove("is-leaving", "is-active", "is-settle");
            shaderEl.style.transform = "scale(0)";
            shaderEl.style.opacity = "0";
          }
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              if (shaderEl) {
                shaderEl.style.transform = "";
                shaderEl.style.opacity = "";
                shaderEl.classList.add("is-active");
              }
              setTimeout(function () {
                if (shaderEl) shaderEl.classList.add("is-settle");
              }, 800);
            });
          });
          if (img) {
            img.style.filter =
              "drop-shadow(0 0 24px " +
              accent +
              ") drop-shadow(0 0 50px " +
              accent +
              ")";
          }
        });

        card.addEventListener("mousemove", function (e) {
          if (tiltRAF) return;
          tiltRAF = requestAnimationFrame(function () {
            tiltRAF = null;
            if (!inner) return;
            var r = inner.getBoundingClientRect();
            inner.style.transform =
              "rotateX(" +
              -(((e.clientY - r.top - r.height / 2) / r.height) * 8) +
              "deg) rotateY(" +
              ((e.clientX - r.left - r.width / 2) / r.width) * 8 +
              "deg)";
            if (glass) {
              glass.style.background =
                "radial-gradient(circle at " +
                (e.clientX - r.left) +
                "px " +
                (e.clientY - r.top) +
                "px,rgba(255,255,255,0.06) 0%,transparent 50%)";
              glass.style.opacity = "1";
            }
          });
        });

        card.addEventListener("mouseleave", function () {
          if (tiltRAF) {
            cancelAnimationFrame(tiltRAF);
            tiltRAF = null;
          }
          if (inner) inner.style.transform = "rotateX(0) rotateY(0)";
          if (glass) glass.style.opacity = "0";
          if (img) img.style.filter = "none";
          if (shaderEl) {
            shaderEl.classList.remove("is-active", "is-settle");
            shaderEl.classList.add("is-leaving");
          }
          leaveTimers[key] = setTimeout(function () {
            if (shaderEl) shaderEl.classList.remove("is-leaving");
            if (active[key]) destroyShader(active[key]);
            delete active[key];
            var idx = activeOrder.indexOf(key);
            if (idx !== -1) activeOrder.splice(idx, 1);
            leaveTimers[key] = null;
          }, 550);
        });
      });
    });
  }

  function initKeyboardCartBtns(cards) {
    cards.forEach(function (card) {
      card.querySelectorAll(".evc__cart-btn").forEach(function (btn) {
        if (btn.dataset.evcKbdInit) return;
        btn.dataset.evcKbdInit = "1";
        btn.addEventListener("keydown", function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            btn.click();
          }
        });
      });
    });
  }

  window.evcInitCardInteractions = function (root) {
    root = root || document;
    if (!root.querySelectorAll) return;
    var all = root.querySelectorAll(".evc__card");
    var cards = Array.prototype.filter.call(all, function (c) {
      return !c.dataset.evcInited;
    });
    if (!cards.length) return;
    cards.forEach(function (c) {
      c.dataset.evcInited = "1";
    });
    initReveal(cards);
    initHoverShader(cards);
    initKeyboardCartBtns(cards);
  };

  /* Add to Cart — unified via window.evAddToCart (drawer-based UX, defined by
     sections/ev-cart-drawer.liquid which is rendered globally in the header group). */
  window.evcATC = function (e, vid) {
    e.preventDefault();
    e.stopPropagation();
    var btn = e.currentTarget;
    var card = btn.closest(".evc__card");
    var title = (card && card.getAttribute("data-title")) || "Produkt";
    var href = (card && card.getAttribute("href")) || "";
    if (!vid || vid === "0" || vid === "") {
      if (href) {
        window.location.href = href;
        return;
      }
      return;
    }
    if (typeof window.evAddToCart === "function") {
      window.evAddToCart(vid, { title: title, btn: btn, openDrawer: true });
    } else {
      var label = btn.querySelector(".evc__cart-label");
      var orig = label ? label.innerHTML : "";
      if (label)
        label.innerHTML =
          '<svg style="width:16px;height:16px;animation:spin .6s linear infinite" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4m0 12v4m-7.07-14.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4m-4.93-7.07l-2.83 2.83m-8.48 8.48l-2.83 2.83"/></svg>';
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] }),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function () {
          window.location.href = "/cart";
        })
        .catch(function () {
          if (label) label.innerHTML = orig;
        });
    }
  };

  /* Buy Now — add to cart, then go straight to /checkout (same flow as .evp__buy-now). */
  window.evcBuyNow = function (e, vid, price) {
    e.preventDefault();
    e.stopPropagation();
    var btn = e.currentTarget;
    var card = btn.closest(".evc__card");
    var title = (card && card.getAttribute("data-title")) || "Produkt";
    if (!vid || vid === "0" || vid === "") {
      if (window.evToast) window.evToast("Variante nicht verfügbar", "err");
      return;
    }
    function toCheckout(data) {
      try {
        if (window.evDL) {
          var _it = (data && data.items && data.items[0]) || null;
          var _val = _it
            ? (_it.final_line_price || _it.line_price || _it.final_price * _it.quantity) / 100
            : (parseInt(price, 10) || 0) / 100;
          var _items = _it
            ? [
                {
                  item_id: _it.sku || _it.variant_id,
                  item_name: _it.product_title || title,
                  item_variant: _it.variant_title || "",
                  item_brand: _it.vendor || "",
                  price: (_it.final_price || _it.price) / 100,
                  quantity: _it.quantity || 1,
                },
              ]
            : [];
          window.evDL("begin_checkout", {
            ecommerce: { currency: (data && data.currency) || "EUR", value: _val, items: _items },
          });
        }
      } catch (_dlErr) {}
      window.location.href = "/checkout";
    }
    if (typeof window.evAddToCart === "function") {
      window.evAddToCart(vid, { title: title, btn: btn, openDrawer: false, onSuccess: toCheckout }).catch(
        function () {},
      );
      return;
    }
    var label = btn.querySelector(".evc__cart-label");
    var orig = label ? label.innerHTML : "";
    if (label)
      label.innerHTML =
        '<svg style="width:16px;height:16px;animation:spin .6s linear infinite" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4m0 12v4m-7.07-14.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4m-4.93-7.07l-2.83 2.83m-8.48 8.48l-2.83 2.83"/></svg>';
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] }),
    })
      .then(function (r) {
        if (!r.ok)
          return r.json().then(function (d) {
            throw d;
          });
        return r.json();
      })
      .then(toCheckout)
      .catch(function (err) {
        if (label) label.innerHTML = orig;
        var msg = err && err.description ? err.description : "Fehler — nochmal versuchen";
        if (window.evToast) window.evToast(msg, "err");
      });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      window.evcInitCardInteractions(document);
    });
  } else {
    window.evcInitCardInteractions(document);
  }
})();
